import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';

import {useRampFlagMediaVideoSyncEnabled} from '../../../util/rampFeatureFlags';
import {useMaybeWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {PublishEventData, SyncEventType} from './types';
import {createStateUpdateCorrelation} from './utils/correlation';
import {createEventProcessingContext, processEvent} from './utils/events';
import {VideoSyncDebugger} from './utils/videoSyncDebugger';

// Context type with stable functions
export type MediaSyncContextType = {
  // Global state
  currentTime: number;
  isPlaying: boolean;
  playbackRate: number;
  lastUpdate: number;
  leaderId: string | null;
  syncThreshold: number;
  isEnabled: boolean;
  autoPlay: boolean;
  autoLoop: boolean;
  correlationId?: string;
  originalEventType?: string;

  // Stable functions
  publishEvent: (event: PublishEventData) => void;
  setSyncThreshold: (threshold: number) => void;
};

const MediaSyncContext = createContext<MediaSyncContextType | null>(null);

// Disabled context for when sync is not available
const disabledMediaSyncContext: MediaSyncContextType = {
  currentTime: 0,
  isPlaying: false,
  playbackRate: 1,
  lastUpdate: performance.now(),
  syncThreshold: 0.01,
  leaderId: null,
  isEnabled: false,
  autoLoop: false,
  autoPlay: false,
  publishEvent: () => {},
  setSyncThreshold: () => {},
};

export const useMediaSyncContextMaybe = () => {
  return useContext(MediaSyncContext) || disabledMediaSyncContext;
};

export const useMediaSyncContext = () => {
  const context = useContext(MediaSyncContext);
  if (!context) {
    throw new Error(
      'useMediaSyncContext must be used within a MediaSyncContextProvider'
    );
  }
  return context;
};

export const MediaSyncContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const entityName = useParams<{entityName: string}>().entityName;
  const isEnabled = useRampFlagMediaVideoSyncEnabled(entityName);

  // autoPlay and autoLoop roll up to workspace settings
  const workspaceSettings = useMaybeWorkspaceSettingsContext();
  const autoPlay =
    workspaceSettings?.media?.workspaceMediaSettings
      ?.videoPlaybackSyncAutoplay ?? false;
  const autoLoop =
    workspaceSettings?.media?.workspaceMediaSettings?.videoPlaybackSyncLoop ??
    false;

  // Simple playback state
  const [playbackState, setPlaybackState] = useState({
    currentTime: 0,
    isPlaying: false,
    playbackRate: 1,
    lastUpdate: performance.now(),
    leaderId: null as string | null,
    correlationId: undefined as string | undefined,
    originalEventType: undefined as string | undefined,
  });
  const playbackStateRef = useRef(playbackState);
  playbackStateRef.current = playbackState;

  const [syncThreshold, setSyncThreshold] = useState(0.01);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRestartingRef = useRef(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    };
  }, []);

  // Simple state update function
  const updatePlaybackState = useCallback(
    (
      updates: Partial<typeof playbackState>,
      correlationId?: string,
      originalEventType?: string
    ) => {
      setPlaybackState(prev => {
        // Ensure currentTime is always finite
        const safeUpdates = {
          ...updates,
          ...(updates.currentTime !== undefined &&
          !isFinite(updates.currentTime)
            ? {currentTime: 0}
            : {}),
        };

        const newState = {
          ...prev,
          ...safeUpdates,
          lastUpdate: performance.now(),
          correlationId: correlationId || prev.correlationId,
          originalEventType: originalEventType || prev.originalEventType,
        };
        VideoSyncDebugger.logStateUpdate(
          newState,
          'MediaSyncContextProvider',
          correlationId
        );
        return newState;
      });
    },
    []
  );

  // Debounced restart function - restarts all videos if no leader claims within timeout
  const debouncedRestart = useCallback(
    (lastEndedVideoId?: string) => {
      if (!autoLoop || isRestartingRef.current) {
        return;
      }

      // Additional safeguard: if we don't have a specific lastEndedVideoId,
      // don't restart to prevent race conditions when all videos end simultaneously
      if (!lastEndedVideoId) {
        return;
      }

      isRestartingRef.current = true;

      // When autoLoop is enabled, we want to restart all videos from the beginning
      if (lastEndedVideoId) {
        // Reset global state to restart all videos from the beginning
        setPlaybackState(prev => ({
          ...prev,
          currentTime: 0, // Reset to beginning for autoLoop restart
          isPlaying: true, // Start playing
          leaderId: lastEndedVideoId, // Make the last ended video the leader
        }));
      } else {
        // Fallback to the old approach if no last ended video ID
        setPlaybackState(prev => ({
          ...prev,
          currentTime: 0,
          isPlaying: true,
          leaderId: null,
        }));
      }

      // Clear the restarting flag after a delay
      setTimeout(() => {
        isRestartingRef.current = false;
      }, 16);
    },
    [autoLoop]
  );

  // Leader election logic when a video ends
  const handleLeaderElection = useCallback(
    (endedVideoId: string) => {
      // If the ended video was the leader, nullify the leader to start election
      if (endedVideoId === playbackState.leaderId) {
        if (restartTimeoutRef.current) {
          clearTimeout(restartTimeoutRef.current);
        }

        // Set leader to null to signal that leadership is available
        // BUT don't reset the global time - keep it at the current time
        setPlaybackState(prev => {
          return {...prev, leaderId: null};
        });

        restartTimeoutRef.current = setTimeout(() => {
          if (
            playbackStateRef.current.leaderId === endedVideoId ||
            playbackStateRef.current.leaderId === null
          ) {
            debouncedRestart(endedVideoId);
          }
        }, 200);
      }
    },
    [playbackState.leaderId, debouncedRestart]
  );

  // Helper function to process events and apply state changes
  const processEventsAndApplyState = useCallback(
    (
      events: PublishEventData[],
      currentLeaderId: string | null,
      handleLeaderElectionFn: (videoId: string) => void,
      chainCorrelationId?: string
    ) => {
      const context = createEventProcessingContext();
      let correlationId: string | undefined = chainCorrelationId;
      let originalEventType: string | undefined;

      // Process all events and track correlation
      events.forEach(event => {
        // If this is a CHAIN event, use the CHAIN's correlation ID for all sub-events
        if (correlationId && !event.correlationId) {
          event.correlationId = correlationId;
        }
        processEvent(event, context, currentLeaderId);
        // Use the first event's correlation ID and type for the state update
        if (!correlationId && event.correlationId) {
          correlationId = event.correlationId;
        }
        if (!originalEventType) {
          originalEventType = event.type;
        }
      });
      // Apply state changes
      if (
        Object.keys(context.batchedUpdates).length > 0 ||
        context.newLeaderId !== null
      ) {
        const finalUpdates = {
          ...context.batchedUpdates,
          ...(context.newLeaderId !== null
            ? {leaderId: context.newLeaderId}
            : {}),
        };

        // Create correlation ID for state update
        const stateCorrelationId = correlationId
          ? createStateUpdateCorrelation(correlationId, 'state_update')
          : undefined;

        updatePlaybackState(
          finalUpdates,
          stateCorrelationId,
          originalEventType
        );
      }

      // Handle leader election if needed
      if (
        context.shouldTriggerLeaderElection &&
        context.leaderElectionVideoId
      ) {
        handleLeaderElectionFn(context.leaderElectionVideoId);
      }
    },
    [updatePlaybackState]
  );

  // Provide function for consumers to publish events
  const publishEvent = useCallback(
    (event: PublishEventData) => {
      // ENDED, LEADER, PAUSE, and SEEKING events should be processed from any video
      // Other events should only be processed from the current leader
      const currentLeaderId = playbackStateRef.current.leaderId;
      if (
        event.type !== SyncEventType.LEADER &&
        event.type !== SyncEventType.ENDED &&
        event.type !== SyncEventType.PAUSE &&
        event.type !== SyncEventType.SEEKING &&
        (!event.videoId || event.videoId !== currentLeaderId)
      ) {
        VideoSyncDebugger.logEvent(
          event.type,
          event.videoId || 'unknown',
          event.data,
          {
            reason: 'not from leader',
            currentLeader: currentLeaderId,
          }
        );
        return;
      }

      processEventsAndApplyState(
        [event],
        currentLeaderId,
        handleLeaderElection,
        event.correlationId
      );
    },
    [processEventsAndApplyState, handleLeaderElection]
  );

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      ...playbackState,
      syncThreshold,
      isEnabled,
      autoLoop,
      autoPlay,
      publishEvent,
      setSyncThreshold,
    }),
    [
      playbackState,
      syncThreshold,
      isEnabled,
      autoPlay,
      autoLoop,
      publishEvent,
      setSyncThreshold,
    ]
  );

  return (
    <MediaSyncContext.Provider value={contextValue}>
      {children}
    </MediaSyncContext.Provider>
  );
};

export {disabledMediaSyncContext};
